'use client';

import { Fragment, startTransition, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronRight, Database, Download, RefreshCw, Search, ShieldAlert } from 'lucide-react';
import { Header } from '@/components/header';
import { Sidebar } from '@/components/sidebar';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import {
  getAccessGuardMetrics,
  getResourceEventSamples,
  MAX_RESOURCE_EVENT_SAMPLES,
  type AccessGuardMetricsEntry,
  type MetricsStorageUsage,
} from '@/lib/urm-api';
import { cn } from '@/lib/utils';
import {
  buildAccessGuardRows,
  aggregateAccessGuardRowsByUser,
  accessGuardRowsToCsv,
  sortAccessGuardRows,
  aggregateResourceEvents,
  buildUpdateSequence,
  writeResourceKey,
  type WriteInvestigation,
  type AccessGuardRow,
} from '@/app/access-guard/logic';
import { WriteEventBreakdown } from '@/app/access-guard/write-event-breakdown';

const PAGE_LIMIT = 1000;
const MAX_PAGES = 100;
const DEFAULT_SAMPLES = '1000';
const ACTIONS = ['read', 'write', 'touch', 'getEvents', 'getHistory'];

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes.toLocaleString()} B`;
  const units = ['KiB', 'MiB', 'GiB', 'TiB'];
  let value = bytes / 1024;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  return `${value.toLocaleString(undefined, { maximumFractionDigits: 1 })} ${units[unitIndex]}`;
}

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

export default function AccessGuardPage() {
  const [entries, setEntries] = useState<AccessGuardMetricsEntry[]>([]);
  const [storage, setStorage] = useState<MetricsStorageUsage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdatedMs, setLastUpdatedMs] = useState<number | null>(null);
  const [query, setQuery] = useState('');
  const [selectedActions, setSelectedActions] = useState<Set<string>>(new Set());
  const [sampleCounts, setSampleCounts] = useState<Record<string, string>>({});
  const [investigations, setInvestigations] = useState<Record<string, WriteInvestigation>>({});
  const [pendingInvestigations, setPendingInvestigations] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const investigationAbortRef = useRef(new Map<string, AbortController>());
  const abortRef = useRef<AbortController | null>(null);
  const runIdRef = useRef(0);

  const load = useCallback(async () => {
    for (const controller of investigationAbortRef.current.values()) controller.abort();
    investigationAbortRef.current.clear();
    setPendingInvestigations(new Set());
    setInvestigations({});
    setSampleCounts({});
    setExpanded(new Set());
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const runId = ++runIdRef.current;
    const isCurrent = () => runIdRef.current === runId && !controller.signal.aborted;

    setLoading(true);
    setError(null);
    try {
      const collected: AccessGuardMetricsEntry[] = [];
      let page = await getAccessGuardMetrics(
        { limit: PAGE_LIMIT, includeStorage: true },
        controller.signal,
      );
      collected.push(...page.entries);
      const nextStorage = page.storage ?? null;
      let cursor = page.next_cursor;
      let pageCount = 1;

      while (page.truncated) {
        if (!cursor) throw new Error('AccessGuard pagination stopped without a cursor');
        if (pageCount >= MAX_PAGES) throw new Error('AccessGuard response exceeded the page limit');
        const previousCursor = cursor;
        page = await getAccessGuardMetrics(
          { limit: PAGE_LIMIT, startAfter: cursor },
          controller.signal,
        );
        collected.push(...page.entries);
        cursor = page.next_cursor;
        pageCount += 1;
        if (page.truncated && cursor === previousCursor) {
          throw new Error('AccessGuard pagination cursor did not advance');
        }
      }

      if (!isCurrent()) return;
      setEntries(collected);
      setStorage(nextStorage);
      setLastUpdatedMs(Date.now());
    } catch (caught: unknown) {
      if (!isCurrent() || isAbortError(caught)) return;
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const investigationControllers = investigationAbortRef.current;
    startTransition(() => { void load(); });
    return () => {
      for (const controller of investigationControllers.values()) controller.abort();
      investigationControllers.clear();
      runIdRef.current += 1;
      abortRef.current?.abort();
    };
  }, [load]);

  const actionCounts = new Map<string, number>();
  let totalViolationCount = 0;
  for (const entry of entries) {
    actionCounts.set(entry.action, (actionCounts.get(entry.action) ?? 0) + entry.count);
    totalViolationCount += entry.count;
  }
  const actions = Array.from(new Set([...ACTIONS, ...actionCounts.keys()])).sort((left, right) =>
    (actionCounts.get(right) ?? 0) - (actionCounts.get(left) ?? 0) || left.localeCompare(right));
  const rows = useMemo(() => buildAccessGuardRows(entries), [entries]);
  const filteredRows = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase();
    return rows.filter((entry) =>
      (selectedActions.size === 0 || selectedActions.has(entry.action))
      && (!normalized || [entry.action, entry.limiter_key, String(entry.count),
        ...Object.values(entry.dimensions).filter((value) => typeof value === 'string'),
      ].some((value) => value.toLocaleLowerCase().includes(normalized))),
    );
  }, [selectedActions, query, rows]);
  const visibleEntries = useMemo(() => sortAccessGuardRows(filteredRows, 'count', 'desc'), [filteredRows]);
  const visibleUserSummaries = useMemo(
    () => aggregateAccessGuardRowsByUser(filteredRows).sort((left, right) =>
      right.count - left.count || (left.user ?? '').localeCompare(right.user ?? '')),
    [filteredRows],
  );

  const rowsByUser = new Map<string | null, AccessGuardRow[]>();
  for (const row of visibleEntries) {
    const resources = rowsByUser.get(row.dimensions.user) ?? [];
    resources.push(row);
    rowsByUser.set(row.dimensions.user, resources);
  }
  const toggleAction = (action: string) => setSelectedActions((previous) => {
    const next = new Set(previous);
    if (next.has(action)) next.delete(action); else next.add(action);
    return next;
  });
  const toggleExpanded = (key: string) => setExpanded((previous) => {
    const next = new Set(previous);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const cancelInvestigation = (key: string) => {
    investigationAbortRef.current.get(key)?.abort();
    investigationAbortRef.current.delete(key);
    setPendingInvestigations((previous) => {
      const next = new Set(previous);
      next.delete(key);
      return next;
    });
  };

  const investigate = async (row: AccessGuardRow, refresh = false) => {
    const key = writeResourceKey(row);
    if (!key) return;
    const samples = Number(sampleCounts[key] ?? DEFAULT_SAMPLES);
    if (!Number.isInteger(samples) || samples < 1 || samples > MAX_RESOURCE_EVENT_SAMPLES) return;
    if (loading || investigationAbortRef.current.has(key)) return;
    const cached = investigations[key];
    if (!refresh && cached && !cached.error) return;
    const controller = new AbortController();
    investigationAbortRef.current.set(key, controller);
    setPendingInvestigations((previous) => new Set(previous).add(key));
    try {
      const { group, version, type, name, path } = row.dimensions;
      const { events, historyExhausted } = await getResourceEventSamples({
        group: group!, version: version!, type: type!, name: name!, path: path!,
      }, samples, controller.signal, true);
      if (controller.signal.aborted) return;
      const result: WriteInvestigation = {
        users: aggregateResourceEvents(events, historyExhausted), count: events.length,
        sequence: buildUpdateSequence(events),
      };
      setInvestigations((previous) => ({ ...previous, [key]: result }));
    } catch (caught) {
      if (controller.signal.aborted) return;
      setInvestigations((previous) => ({ ...previous, [key]: {
        users: [], sequence: [], count: 0,
        error: caught instanceof Error ? caught.message : String(caught),
      } }));
    } finally {
      if (investigationAbortRef.current.get(key) === controller) {
        investigationAbortRef.current.delete(key);
        setPendingInvestigations((previous) => {
          const next = new Set(previous);
          next.delete(key);
          return next;
        });
      }
    }
  };

  const openResource = (row: AccessGuardRow) => {
    const rowKey = row.action + ':' + row.limiter_key;
    toggleExpanded(rowKey);
    if (!expanded.has(rowKey)) void investigate(row);
  };

  const exportVisibleRows = () => {
    const csv = accessGuardRowsToCsv(visibleEntries);
    const blob = new Blob([csv], {
      type: 'text/csv;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'access-guard-violations-' + new Date().toISOString().slice(0, 10) + '.csv';
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };
  const visibleCount = visibleUserSummaries.length;

  return (
    <div className="flex h-screen">
      <Sidebar />
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <Header />
        <main className="flex-1 overflow-auto p-6">
          <div className="mx-auto max-w-7xl space-y-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2">
                  <ShieldAlert className="h-7 w-7 text-destructive" aria-hidden="true" />
                  <h1 className="text-3xl font-bold">Access Guard Violations</h1>
                </div>
                <p className="mt-1 text-sm text-muted-foreground">
                  Limit-reached events retained in the live AccessGuard metrics store.
                </p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => exportVisibleRows()} disabled={visibleCount === 0}>
                  <Download aria-hidden="true" />
                  Export CSV
                </Button>
                <Button variant="outline" onClick={() => void load()} disabled={loading}>
                  <RefreshCw className={cn('h-4 w-4', loading && 'animate-spin')} aria-hidden="true" />
                  {loading ? 'Loading…' : 'Refresh'}
                </Button>
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
              <p className="flex flex-wrap items-center gap-2">
                <Database className="h-3.5 w-3.5" aria-hidden="true" />
                <span>Redis storage</span>
                <span className="font-medium text-foreground tabular-nums">
                  {storage ? (storage.approximate ? '≈' : '') + formatBytes(storage.redis_memory_bytes) : 'Unavailable'}
                </span>
                {storage && <span>· {storage.redis_key_count.toLocaleString()} retained daily keys</span>}
              </p>
              {lastUpdatedMs && <p>Updated {new Date(lastUpdatedMs).toLocaleTimeString()}</p>}
            </div>

            {error && (
              <div role="alert" className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
                <p className="font-semibold">Could not load AccessGuard violations</p>
                <p className="mt-1">{error}</p>
              </div>
            )}

            <Card className="overflow-hidden shadow-sm">
              <CardHeader className="space-y-0 border-b p-0">
                <div className="bg-muted/25 px-6 py-4">
                  <label className="block space-y-1.5">
                    <span className="block text-xs font-medium text-muted-foreground">Search</span>
                    <span className="relative block">
                      <Search className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-muted-foreground" aria-hidden="true" />
                      <input type="search" aria-label="Search violations" value={query}
                        onChange={(event) => setQuery(event.target.value)}
                        placeholder="Search users, paths, names, types, actions…"
                        className="h-10 w-full rounded-md border bg-background pl-9 pr-3 text-sm shadow-sm outline-none transition-shadow focus:ring-2 focus:ring-ring" />
                    </span>
                  </label>
                  <div role="group" aria-label="Filter by action" className="mt-3 flex flex-wrap items-center gap-2">
                    <Button variant={selectedActions.size === 0 ? 'default' : 'outline'} size="sm"
                      aria-pressed={selectedActions.size === 0} onClick={() => setSelectedActions(new Set())}>
                      All <span className="rounded bg-foreground/10 px-1.5 py-0.5 text-xs tabular-nums">{totalViolationCount.toLocaleString()}</span>
                    </Button>
                    {actions.map((action) => <Button key={action} size="sm"
                        variant={selectedActions.has(action) ? 'default' : 'outline'}
                        aria-pressed={selectedActions.has(action)} onClick={() => toggleAction(action)}>
                        {action.charAt(0).toUpperCase() + action.slice(1)}{' '}
                        <span className="rounded bg-foreground/10 px-1.5 py-0.5 text-xs tabular-nums">{(actionCounts.get(action) ?? 0).toLocaleString()}</span>
                      </Button>)}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="px-0 pb-0">
                <div className="overflow-x-auto" aria-busy={loading}>
                  <table className="w-full table-fixed text-left text-sm">
                    <thead className="border-b bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
                      <tr>
                        <th className="px-6 py-3.5 font-medium">User</th>
                        <th className="w-36 px-6 py-3.5 text-right font-medium">Violations</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleUserSummaries.map((summary) => {
                        const summaryKey = 'user:' + (summary.user ?? '');
                        const resources = rowsByUser.get(summary.user) ?? [];
                        return <Fragment key={summaryKey}>
                          <tr className="border-b transition-colors last:border-0 hover:bg-muted/30">
                            <td className="px-6 py-4">
                              <button type="button" className="flex w-full items-center gap-2 text-left font-medium [overflow-wrap:anywhere] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                aria-expanded={expanded.has(summaryKey)} onClick={() => toggleExpanded(summaryKey)}>
                                <ChevronRight aria-hidden="true" className={cn('h-4 w-4 shrink-0 text-muted-foreground transition-transform', expanded.has(summaryKey) && 'rotate-90')} />
                                {summary.user ?? 'Resource-scoped (no user)'}
                              </button>
                            </td>
                            <td className="px-6 py-4 text-right text-base font-semibold tabular-nums">{summary.count.toLocaleString()}</td>
                          </tr>
                          {expanded.has(summaryKey) && <tr className="border-b bg-muted/20">
                            <td colSpan={2} className="p-3 sm:p-5">
                              <div className="min-w-0 space-y-3">
                                {resources.map((row) => {
                                  const rowKey = row.action + ':' + row.limiter_key;
                                  const resourceKey = writeResourceKey(row);
                                  const { path, name, group, version, type, recognized } = row.dimensions;
                                  return <article key={rowKey} aria-label={row.action + ' violation: ' + (name ?? row.limiter_key)}
                                    className="min-w-0 overflow-hidden rounded-xl border bg-background shadow-sm">
                                    <div className="space-y-3 p-4">
                                      <div className="flex flex-wrap items-center justify-between gap-3">
                                        <Badge variant="outline" className="uppercase tracking-wide">{row.action}</Badge>
                                        <div className="flex items-center gap-3">
                                          <span className="text-xs text-muted-foreground"><strong className="text-sm text-foreground tabular-nums">{row.count.toLocaleString()}</strong> violations</span>
                                          {resourceKey && <Button type="button" variant="outline" size="sm" disabled={loading}
                                            className="shadow-[0_0_10px_2px_rgba(239,68,68,0.3)] hover:shadow-[0_0_14px_3px_rgba(239,68,68,0.4)]"
                                            aria-label={'Investigate ' + type + ' / ' + name}
                                            aria-expanded={expanded.has(rowKey)} onClick={() => openResource(row)}>
                                            Investigate
                                            <ChevronRight aria-hidden="true" className={cn('transition-transform', expanded.has(rowKey) && 'rotate-90')} />
                                          </Button>}
                                        </div>
                                      </div>
                                      {recognized && name ? <>
                                        <div className="flex min-w-0 flex-wrap items-center gap-2 [overflow-wrap:anywhere]">
                                          <span className="min-w-0 rounded-md bg-blue-50 px-2.5 py-1.5 font-mono text-xs text-blue-900 dark:bg-blue-950 dark:text-blue-100">{path}</span>
                                          <span aria-hidden="true" className="text-muted-foreground">/</span>
                                          <span className="min-w-0 rounded-md bg-emerald-50 px-2.5 py-1.5 font-semibold text-emerald-900 dark:bg-emerald-950 dark:text-emerald-100">{name}</span>
                                        </div>
                                        <dl className="flex flex-wrap gap-x-5 gap-y-2 text-xs [overflow-wrap:anywhere]">
                                          {[['Group', group], ['Version', version], ['Type', type]].map(([label, value]) => <div key={label} className="min-w-0">
                                            <dt className="mb-0.5 text-muted-foreground">{label}</dt><dd className="font-medium">{value}</dd>
                                          </div>)}
                                        </dl>
                                      </> : recognized ? <p className="text-muted-foreground">All resources</p> : <div>
                                        <p className="text-xs font-medium text-amber-700">Unparsed limiter key</p>
                                        <p className="mt-1 break-all font-mono text-xs">{row.limiter_key}</p>
                                      </div>}
                                    </div>
                                    {resourceKey && expanded.has(rowKey) && <div className="min-w-0 overflow-x-auto border-t p-3 sm:p-4">
                                      <WriteEventBreakdown row={row} result={investigations[resourceKey]}
                                        loading={pendingInvestigations.has(resourceKey)}
                                        samples={sampleCounts[resourceKey] ?? DEFAULT_SAMPLES}
                                        onSamplesChange={(value) => setSampleCounts((previous) => ({ ...previous, [resourceKey]: value }))}
                                        onRefresh={() => void investigate(row, true)}
                                        onCancel={() => cancelInvestigation(resourceKey)} />
                                    </div>}
                                  </article>;
                                })}
                              </div>
                            </td>
                          </tr>}
                        </Fragment>;
                      })}
                    </tbody>
                  </table>
                  {!loading && visibleCount === 0 && (
                    <div className="px-6 py-12 text-center text-sm text-muted-foreground">
                      {entries.length === 0 ? 'No AccessGuard violations are currently retained.' : 'No violations match the filters.'}
                    </div>
                  )}
                  {loading && entries.length === 0 && (
                    <div role="status" className="px-6 py-12 text-center text-sm text-muted-foreground">
                      Loading AccessGuard violations…
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    </div>
  );
}
